import { Box, Card, Typography } from '@mui/material';
import { useContent } from 'contexts/Content/Content';
import { useGame } from 'contexts/Game/Game';
import { usePage } from 'contexts/Page/Page';
import { useTheme } from 'contexts/Theme/Theme';
import { formatMoney, getCardNames, getPercentage, getShallowEqual, getUrl } from 'helpers';
import { useEffect, useRef, useState } from 'react';
import * as setup from 'setup';
import GamePromo from '../GamePromo/GamePromo';
import Image from '../Image/Image';
import RsbCta from '../RsbCta/RsbCta';
import WizardButton from '../WizardButton/WizardButton';

export default function Wizard({ children, highlight, lesson, variables, onAction }: WizardProps) {
    const [buttons, setButtons] = useState<WizardButtonProps[]>([]);
    const [gameVariables, setGameVariables] = useState<Variables | null>(null);
    const [lessonContent, setLessonContent] = useState<WizardContent | null>(null);
    const [ready, setReady] = useState(false);
    const [text, setText] = useState('');
    const [title, setTitle] = useState('');
    const [vars, setVars] = useState<Variables | null>(null);
    const content = useContent();
    const game = useGame();
    const page = usePage();
    const theme = useTheme();
    const titleRef = useRef<any>();

    useEffect(() => {
        // Get game variables
        const results = game.resultType && setup.game.results[game.resultType];
        const newGameVariables = {
            budget: game.budget,
            communityCards: game.communityCards.join(','),
            currentPot: formatMoney(game.currentPot),
            expectedPot: formatMoney(game.expectedPot),
            expectedValue: formatMoney(game.expectedValue),
            holeCards: game.holeCards.join(','),
            holeHandName: game.holeHandName || '…',
            holeCardNames: getCardNames(game.holeCards),
            opponentHandName: game.opponentsHandNames[0] || '…',
            opponentCardNames: getCardNames(game.opponentsCards[0] ?? []),
            remainingCosts: formatMoney(game.remainingCosts),
            resultMessage: results?.message || '…',
            resultTitle: results?.title || '…',
            round: game.round,
            roundCost: formatMoney(game.roundCost),
            winPercentage: getPercentage(game.winProbability),
        };
        if (!gameVariables || !getShallowEqual(newGameVariables, gameVariables)) {
            setGameVariables(newGameVariables);
        }
    }, [
        content,
        game.budget,
        game.communityCards,
        game.currentPot,
        game.expectedPot,
        game.expectedValue,
        game.holeCards,
        game.holeHandName,
        game.opponentsCards,
        game.opponentsHandNames,
        game.remainingCosts,
        game.resultType,
        game.round,
        game.roundCost,
        game.winProbability,
        gameVariables,
    ]);

    useEffect(() => {
        if (gameVariables && (variables !== null || !page.lesson)) {
            // Merge variables
            const newVars = {
                ...gameVariables,
                ...variables,
            };
            setVars(newVars);
        }
    }, [gameVariables, page.lesson, variables]);

    useEffect(() => {
        // Get lesson content
        const newLessonContent: WizardContent | null =
            (lesson && content.lessons[lesson]) || (page.lesson && content.lessons[page.lesson]) || null;
        setLessonContent(newLessonContent);
    }, [content.lessons, lesson, page.lesson]);

    useEffect(() => {
        if (lessonContent && vars) {
            // Parse buttons
            const newButtons = [
                lessonContent.button1,
                lessonContent.button2,
                lessonContent.button3,
                lessonContent.button4,
            ]
                .filter(button => button)
                .map(
                    button =>
                        ({
                            ...button,
                            label: content.parseHtml(button?.label || '', vars),
                        }) as WizardButtonProps
                );
            setButtons(newButtons);

            // Parse text
            const newText = content.parseHtml(lessonContent?.text || '', vars);
            setText(newText);

            // Parse title
            const newTitle = content.parseHtml(lessonContent?.title || '', vars);
            setTitle(newTitle);

            // Set ready
            if (titleRef.current && !ready) {
                titleRef.current.focus();
                setReady(true);
            }
        }
    }, [content, lessonContent, page.lesson, ready, vars]);

    if ((!lessonContent?.text && !lessonContent?.title) || !vars) {
        return null;
    }

    return (
        <>
            {lessonContent && (
                <Card
                    elevation={0}
                    sx={{
                        boxShadow: '0px 2px 40px rgba(0, 0, 0, 0.1)',
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    <Box display="flex" flexDirection="column" gap={2.5} paddingX={highlight ? 5 : 2.5} paddingY={2.5}>
                        <Box display="flex" flexDirection="column-reverse" gap={2.5}>
                            <Typography
                                align={highlight ? 'center' : 'left'}
                                color={theme.darkMode ? '#FFF' : '#E24636'}
                                component="h1"
                                dangerouslySetInnerHTML={{ __html: title }}
                                ref={titleRef}
                                variant="montserrat529"
                            />
                            {lessonContent?.image && (
                                <Box marginX="auto" maxWidth={340} width="100%">
                                    <Box paddingBottom={`${(170 / 360) * 100}%`} position="relative">
                                        <Box
                                            alignItems="center"
                                            display="flex"
                                            height="100%"
                                            justifyContent="center"
                                            left={0}
                                            position="absolute"
                                            top={0}
                                            width="100%"
                                        >
                                            <picture>
                                                <img
                                                    alt={lessonContent?.imageAlt ?? ''}
                                                    src={getUrl(lessonContent.image)}
                                                    style={{ maxWidth: '100%' }}
                                                />
                                            </picture>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                        {text && (
                            <Typography
                                dangerouslySetInnerHTML={{ __html: text }}
                                display="flex"
                                flexDirection="column"
                                gap={2}
                                marginY={highlight ? 2 : 0}
                                variant={highlight ? 'montserrat524' : 'montserrat518'}
                            />
                        )}
                        {children}
                        {!!buttons.length && (
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={3}
                                marginX={highlight ? 'auto' : undefined}
                                marginY={highlight ? 2 : 0}
                                width={highlight ? 360 : undefined}
                            >
                                {buttons.map((button, index) => (
                                    <WizardButton key={index} {...button} onAction={onAction} />
                                ))}
                            </Box>
                        )}
                        {content.game.promo && lessonContent.promo && (
                            <Box alignItems="center" display="flex" flexDirection="column" gap={2}>
                                {highlight && <Typography variant="montserrat524">or</Typography>}
                                <GamePromo />
                            </Box>
                        )}
                        {lessonContent.promo && (
                            <Box alignItems="center" display="flex" flexDirection="column" gap={2}>
                                <RsbCta />
                            </Box>
                        )}
                    </Box>
                </Card>
            )}
        </>
    );
}
